import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { Field, useForm } from 'react-final-form';
import { sqWorksheetStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { DateTimeEntry } from '@/datetime/DateTimeEntry.atom';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldWrapperProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { faTimes } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export interface DatePickerFormComponentProps extends ValidatingFormComponent<any> {
  component: 'DatePickerFormComponent';
  id?: string;
  value: moment.Moment | undefined;
  placeholder?: string;
  labelIcon?: string;
  labelIconAction?: () => void;
  labelIconTooltip?: string;
  labelClassNames?: string;
  clearDate?: () => void;
}
export const DatePickerFormComponent = (props: DatePickerFormComponentProps) => {
  const {
    name,
    extendValidation,
    validation,
    onChange,
    testId = 'formControl',
    required = false,
    customErrorText,
    labelIcon,
    labelIconAction,
    labelIconTooltip,
    labelClassNames,
    value,
  } = props;

  const timezone = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.timezone);
  const defaultValidation = (value: moment.Moment | undefined) => !!required && !value;
  const [internalValue, setInternalValue] = useState<moment.Moment | undefined>(value ? moment(value) : undefined);
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);
  const formState = useForm().getState();
  const showError =
    _.has(formState.errors, name) &&
    formState.hasValidationErrors &&
    (formState.submitFailed || (formState.modified && formState.modified[name]));

  useEffect(() => {
    setInternalValue(value ? moment(value) : undefined);
  }, [value]);

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props, ['id'])}
      testId={`${testId}-wrap`}
      labelIcon={labelIcon}
      labelIconAction={labelIconAction}
      labelIconTooltip={labelIconTooltip}
      labelClassNames={labelClassNames}
      showError={showError}
      customErrorText={customErrorText}>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          return (
            <div className="flexColumnContainer tw:relative tw:items-center">
              <DateTimeEntry
                testId={testId}
                showError={showError}
                datePickerExtraClassNames="width-120"
                extraClassName={showError ? 'error-border' : ''}
                date={internalValue}
                timezone={timezone}
                hideTimezone={true}
                dateFormat="l"
                inputHeight={35}
                updateDate={(date: moment.Moment | undefined) => {
                  input.onChange(date);
                  onChange && onChange(date);
                }}
              />
              <Icon
                icon={faTimes}
                extraClassNames="mln18 mt3"
                testId={`${testId}Clear`}
                onClick={() => {
                  input.onChange(undefined);
                  onChange && onChange(undefined);
                }}
              />
            </div>
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
